import React, { useState, useEffect } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";
import { formatDate, parseFlexibleDate } from "../lib/date";
import { Calendar as CalendarIcon, X } from "lucide-react";

const fieldCls = "w-full bg-[#0A0B10] border border-[#1E2233] focus:border-[#9D4CDD] outline-none text-white px-3 py-2 rounded-md text-sm transition-colors flex items-center justify-between gap-2";

const toISO = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const parseISO = (s) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || "");
    return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : undefined;
};

// Date picker. Default: a read-only trigger that reads "June 6th, 2026" and picks
// on a calendar. Pass `editable` to also allow typing the date directly (accepts
// YYYY-MM-DD or natural formats). Stores ISO yyyy-mm-dd.
export function DatePicker({
    value,
    onChange,
    placeholder = "Select a date",
    testId,
    fromYear = 1980,
    toYear = new Date().getFullYear() + 6,
    allowClear = true,
    disabled = false,
    editable = false,
    className = "",
}) {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState(value || "");
    useEffect(() => { setText(value || ""); }, [value]);
    const selected = parseISO(value);

    const commitText = () => {
        const raw = text.trim();
        if (!raw) { if (value) onChange(""); return; }
        const d = parseFlexibleDate(raw);
        if (d && !isNaN(d.getTime())) {
            const iso = toISO(d);
            onChange(iso);
            setText(iso);
        } else {
            setText(value || "");   // revert unparseable input
        }
    };

    const calendar = (
        <PopoverContent className="w-auto p-0 bg-[#0A0B10] border border-[#1E2233] text-white" align="start">
            <Calendar
                mode="single"
                selected={selected}
                defaultMonth={selected || new Date()}
                onSelect={(d) => { if (d) { onChange(toISO(d)); setText(toISO(d)); setOpen(false); } }}
                captionLayout="buttons"
                fromYear={fromYear}
                toYear={toYear}
            />
        </PopoverContent>
    );

    if (editable) {
        return (
            <Popover open={open} onOpenChange={setOpen}>
                <div className={`${fieldCls} ${className} ${disabled ? "opacity-50" : "hover:border-[#9D4CDD]"}`}>
                    <input
                        type="text"
                        value={text}
                        disabled={disabled}
                        onChange={(e) => setText(e.target.value)}
                        onBlur={commitText}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitText(); } }}
                        placeholder={placeholder + " (YYYY-MM-DD)"}
                        className="bg-transparent outline-none flex-1 min-w-0 text-white placeholder:text-[#64748B]"
                        data-testid={testId}
                    />
                    <span className="flex items-center gap-1 shrink-0">
                        {allowClear && value ? (
                            <X className="h-3.5 w-3.5 text-[#64748B] hover:text-white cursor-pointer" onClick={() => { onChange(""); setText(""); }} data-testid={testId ? `${testId}-clear` : undefined} />
                        ) : null}
                        <PopoverTrigger asChild>
                            <button type="button" disabled={disabled} className="text-[#94A3B8] hover:text-white" data-testid={testId ? `${testId}-calendar-btn` : undefined}>
                                <CalendarIcon className="h-4 w-4" />
                            </button>
                        </PopoverTrigger>
                    </span>
                </div>
                {calendar}
            </Popover>
        );
    }

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    disabled={disabled}
                    className={`${fieldCls} ${className} ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-[#9D4CDD]"}`}
                    data-testid={testId}
                >
                    <span className={selected ? "" : "text-[#64748B]"} data-testid={testId ? `${testId}-label` : undefined}>
                        {selected ? formatDate(value, { comma: true }) : placeholder}
                    </span>
                    <span className="flex items-center gap-1 shrink-0">
                        {allowClear && value ? (
                            <X
                                className="h-3.5 w-3.5 text-[#64748B] hover:text-white"
                                onClick={(e) => { e.stopPropagation(); onChange(""); }}
                                data-testid={testId ? `${testId}-clear` : undefined}
                            />
                        ) : null}
                        <CalendarIcon className="h-4 w-4 text-[#94A3B8]" />
                    </span>
                </button>
            </PopoverTrigger>
            {calendar}
        </Popover>
    );
}

export default DatePicker;
